import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  onSnapshot,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import {
  getAuth,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from 'firebase/auth';
import { pinyin } from 'pinyin-pro';
import { toast } from 'sonner';
import { logDiag } from './diagnostics';

/**
 * Auth handler origin. Defaults to Firebase's own domain, which is
 * third-party relative to this site — Safari's storage partitioning is what
 * stops signInWithRedirect returning a result there, so Google sign-in cannot
 * complete inside an installed iOS PWA.
 *
 * Setting REACT_APP_AUTH_DOMAIN to our own domain makes the handler
 * first-party and fixes that. It is opt-in per Vercel environment rather than
 * hardcoded, because switching it REQUIRES that
 * https://<domain>/__/auth/handler is registered as an authorized redirect URI
 * on the Google OAuth client first. Until that env var is set, behaviour is
 * unchanged, and unsetting it rolls back without a code deploy.
 *
 * The /__/auth/* rewrite that proxies to Firebase lives in vercel.json.
 */
const AUTH_DOMAIN =
  process.env.REACT_APP_AUTH_DOMAIN || 'wedding-checkin-53e0e.firebaseapp.com';

const firebaseConfig = {
  apiKey: "AIzaSyBwd_ou_4P3h_j35-HXTomoKL8sB9Wr03o",
  authDomain: AUTH_DOMAIN,
  projectId: "wedding-checkin-53e0e",
  storageBucket: "wedding-checkin-53e0e.firebasestorage.app",
  messagingSenderId: "411157912685",
  appId: "1:411157912685:web:ec3e4a1ec59c5d5afab364",
  measurementId: "G-JSPHB11L42"
};

const app = initializeApp(firebaseConfig);

/**
 * Enable IndexedDB-backed offline persistence when the user enters the app
 * via a /live/* URL (the coordinator's day-of surface). Plan Mode sessions
 * skip persistence so copy and config changes are always fresh.
 *
 * Gating is by first-load pathname because Firestore can only be configured
 * once, before any reads. Users who navigate into /live via client-side
 * routing won't get persistence until they refresh — that's acceptable for
 * the "Couple peeking at Live Mode" case since they're typically online.
 */
const wantsPersistence =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/live');

let _db;
if (wantsPersistence) {
  try {
    _db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch (err) {
    console.warn('[firebase] persistent cache init failed, falling back to memory:', err);
    _db = getFirestore(app);
  }
} else {
  _db = getFirestore(app);
}
export const db = _db;

export const storage = getStorage(app);
export const auth = getAuth(app);


const COUPLE_AUTH_EMAIL_KEY = 'gw-couple-auth-email';

/**
 * Send a sign-in magic link to the couple's email. The link returns the user
 * to /couple-auth/complete where completeCoupleSignIn() finalizes the session.
 * Stores the email in localStorage so the completion step can match it.
 */
export const sendCoupleSignInLink = async (email, returnPath = '/') => {
  const url = new URL(window.location.origin + '/couple-auth/complete');
  if (returnPath) url.searchParams.set('return', returnPath);
  await sendSignInLinkToEmail(auth, email, {
    url: url.toString(),
    handleCodeInApp: true,
  });
  try { window.localStorage.setItem(COUPLE_AUTH_EMAIL_KEY, email); } catch {}
};

/**
 * Complete the magic-link sign-in. Called on /couple-auth/complete page load.
 * Returns the signed-in user, or null if this URL isn't a sign-in link.
 * The caller should redirect to the `return` query param on success.
 */
export const completeCoupleSignIn = async () => {
  if (!isSignInWithEmailLink(auth, window.location.href)) return null;
  let email = null;
  try { email = window.localStorage.getItem(COUPLE_AUTH_EMAIL_KEY); } catch {}
  if (!email) {
    // Fallback — same-device assumption fails (e.g. user opened link on a
    // different device). Prompt the caller to ask for the email.
    throw new Error('email-needed');
  }
  const result = await signInWithEmailLink(auth, email, window.location.href);
  try { window.localStorage.removeItem(COUPLE_AUTH_EMAIL_KEY); } catch {}
  return result.user;
};

/**
 * Sign in with Google. Tries popup first; on browsers that block popups
 * (Safari, in-app webviews), falls back to redirect. Throws on user cancel.
 *
 * Caller is responsible for handling the redirect leg via consumeGoogleRedirect()
 * on /couple-auth/complete.
 */
export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (err) {
    const code = err?.code;
    if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user' || code === 'auth/operation-not-supported-in-this-environment') {
      try { window.localStorage.setItem(COUPLE_AUTH_RETURN_KEY, window.location.pathname + window.location.search); } catch {}
      await signInWithRedirect(auth, provider);
      return null;
    }
    throw err;
  }
};

/**
 * Consume a pending Google redirect result. Call on /couple-auth/complete.
 * Returns the signed-in user (and the stored return path) if a redirect was
 * pending, or null if not.
 */
export const consumeGoogleRedirect = async () => {
  const result = await getRedirectResult(auth);
  if (!result?.user) return null;
  let returnPath = '/';
  try {
    const stored = window.localStorage.getItem(COUPLE_AUTH_RETURN_KEY);
    if (stored) returnPath = stored;
    window.localStorage.removeItem(COUPLE_AUTH_RETURN_KEY);
  } catch {}
  return { user: result.user, returnPath };
};

const COUPLE_AUTH_RETURN_KEY = 'gw-couple-auth-return';

/**
 * Wraps onSnapshot with an error handler that toasts on failure.
 * Deduplicates error toasts so only one shows per 10 seconds.
 */
let lastErrorToast = 0;
/* ── Generate a 10-char lowercase alphanumeric RSVP token ── */
const RSVP_TOKEN_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
// Group RSVP code ("booking reference") — 8 uppercase chars from an
// unambiguous alphabet (no I/L/O/0/1). Typed by guests, so it must be easy
// to read aloud; uppercase keeps it disjoint from lowercase rsvpTokens in
// the shared tokenIndex. Mirrors generateGroupCode in api/_firebase.js.
const GROUP_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const generateGroupCode = () => {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => GROUP_CODE_ALPHABET[b % GROUP_CODE_ALPHABET.length]).join('');
};

// Display form: XXXX-XXXX
export const formatGroupCode = (code) =>
  code && code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : (code || '');

// Mirror of normalizeGroupCode in api/_firebase.js — accepts guest-typed
// forms (lowercase, XXXX-XXXX, stray spaces); '' when it can't be a code.
export const normalizeGroupCode = (raw) => {
  if (!raw) return '';
  const code = raw.toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (code.length !== 8) return '';
  if (/[ILO01]/.test(code)) return '';
  return code;
};

export const generateRsvpToken = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(10));
  return Array.from(bytes, b => RSVP_TOKEN_ALPHABET[b % RSVP_TOKEN_ALPHABET.length]).join('');
};

/* ── Generate a 26-char lowercase alphanumeric wedding ID ── */
/* 26 bytes through a 36-char alphabet gives ~138 bits of entropy (better than legacy 8-char random) */
export const generateWeddingId = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(26));
  return Array.from(bytes, b => RSVP_TOKEN_ALPHABET[b % RSVP_TOKEN_ALPHABET.length]).join('');
};

/* ── Generate URL-safe slug from couple names ── */
export const generateRsvpSlug = (coupleName) => {
  if (!coupleName) return '';
  return coupleName
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
};

/* ── Fuzzy name matching for guest search ── */
const NICKNAMES = {
  sue: 'susan', mike: 'michael', bob: 'robert', bill: 'william', liz: 'elizabeth',
  jen: 'jennifer', tom: 'thomas', dan: 'daniel', sam: 'samuel', kate: 'catherine',
  alex: 'alexander', matt: 'matthew', chris: 'christopher', nick: 'nicholas', tony: 'anthony',
  dave: 'david', steve: 'steven', jim: 'james', joe: 'joseph', ben: 'benjamin',
};

/* Equivalent romanizations of common Singaporean Chinese surnames. A single
   character (e.g. 陈) is spelled differently depending on whether the family
   romanizes by Hokkien/Teochew (Tan), Cantonese (Chan), or Mandarin (Chen).
   Search should find the same person regardless of which spelling the user
   types. Each group lists all romanizations that mean the same surname.
   "Wong" is shared between 王 and 黄 in practice; we place it with 王 so
   queries like "wang" still surface Wongs — cross-surname fuzziness is
   accepted over missing results. */
const SURNAME_ALIAS_GROUPS = [
  ['tan', 'chen', 'chan'],              // 陈
  ['lim', 'lin'],                       // 林
  ['ng', 'huang', 'wee'],               // 黄
  ['wong', 'ong', 'wang', 'heng'],      // 王 (Wong also covers 黄-Cantonese)
  ['teo', 'zhang', 'cheung', 'teoh'],   // 张
  ['koh', 'xu', 'hui'],                 // 许
  ['chua', 'cai', 'choi'],              // 蔡
  ['loh', 'lu', 'lo'],                  // 卢
  ['yeo', 'yang', 'yeung'],             // 杨
  ['tay', 'zheng', 'cheng'],            // 郑
  ['sim', 'shen'],                      // 沈
  ['lau', 'liu'],                       // 刘
  ['goh', 'wu'],                        // 吴
  ['lee', 'li'],                        // 李
  ['ho', 'he'],                         // 何
  ['chew', 'zhou', 'chow'],             // 周
  ['chong', 'zhuang'],                  // 庄
  ['yap', 'ye', 'yip'],                 // 叶
  ['khoo', 'qiu', 'chiu'],              // 邱
  ['soh', 'su'],                        // 苏
  ['neo', 'liang'],                     // 梁
];
const SURNAME_ALIAS_MAP = (() => {
  const m = new Map();
  SURNAME_ALIAS_GROUPS.forEach((group) => {
    group.forEach((alias) => m.set(alias, group));
  });
  return m;
})();

const scoreMatchCore = (n, words, q) => {
  if (n === q) return 100;
  if (words.some((w) => w === q)) return 80;
  if (words[0]?.startsWith(q)) return 70;
  if (words.slice(1).some((w) => w.startsWith(q))) return 60;
  const expanded = NICKNAMES[q];
  if (expanded && (words.some((w) => w === expanded) || n.includes(expanded))) return 50;
  const contracted = Object.entries(NICKNAMES).find(([, v]) => v === q);
  if (contracted && (words.some((w) => w === contracted[0]) || n.includes(contracted[0]))) return 50;
  if (n.includes(q)) return 30;
  return 0;
};

/* Convert a name to lowercase Hanyu Pinyin, preserving Latin segments as-is.
   Used as a fallback search index so queries like "wenfa" or "chenwenfa" find
   a guest named "Tan Boon Huat 陈文发". Results are cached per name since the
   conversion cost is non-trivial for long lists. */
const pinyinCache = new Map();
const toPinyinNormalized = (text) => {
  if (pinyinCache.has(text)) return pinyinCache.get(text);
  const converted = pinyin(text, { toneType: 'none', nonZh: 'consecutive' });
  const normalized = converted.toLowerCase().replace(/\s+/g, ' ').trim();
  pinyinCache.set(text, normalized);
  return normalized;
};

/* Returns true if the query is long enough to use for guest search.
   Latin queries need 2+ characters (single letters are too noisy); a single
   CJK character is a whole surname, so it's accepted. */
export const isSearchQueryUsable = (query) => {
  if (!query) return false;
  const q = query.trim();
  if (!q) return false;
  if (q.length >= 2) return true;
  return q.charCodeAt(0) >= 128;
};

/* Returns a relevance score (0 = no match, higher = better) */
export const scoreMatch = (name, query) => {
  if (!query || !name) return 0;
  const q = query.toLowerCase().trim();
  // Reject single-letter Latin queries (too noisy), but allow single
  // non-ASCII queries — a single Chinese character is a meaningful surname.
  if (q.length < 2 && q.charCodeAt(0) < 128) return 0;
  if (q.length < 1) return 0;
  const n = name.toLowerCase();
  const words = n.split(/\s+/);

  const direct = scoreMatchCore(n, words, q);
  if (direct > 0) return direct;

  // Surname dialect aliasing — e.g. "chen" matches a guest named "Tan Boon Huat".
  // Aliased hits score below direct hits so literal matches still rank first.
  const aliases = SURNAME_ALIAS_MAP.get(q);
  if (aliases) {
    let best = 0;
    for (const alias of aliases) {
      if (alias === q) continue;
      const s = scoreMatchCore(n, words, alias);
      if (s > best) best = s;
    }
    if (best > 0) return Math.max(10, best - 15);
  }

  // Pinyin fallback — convert the name's Chinese characters to Hanyu Pinyin
  // and re-try matching. Covers given-name queries like "wenfa" or full-name
  // Pinyin like "chenwenfa" that the alias table (surnames only) can't.
  const nPy = toPinyinNormalized(name);
  if (nPy !== n) {
    const pyWords = nPy.split(/\s+/);
    const pyScore = scoreMatchCore(nPy, pyWords, q);
    if (pyScore > 0) return Math.max(10, pyScore - 20);

    // Whitespace-insensitive fallback — handles "chenwenfa" and "chen wenfa".
    const qNoSpace = q.replace(/\s+/g, '');
    if (qNoSpace.length >= 2) {
      const nPyNoSpace = nPy.replace(/\s+/g, '');
      if (nPyNoSpace.includes(qNoSpace)) {
        return nPyNoSpace.startsWith(qNoSpace) ? 30 : 15;
      }
    }
  }
  return 0;
};

export const fuzzyMatchName = (name, query) => scoreMatch(name, query) > 0;

export const onSnapshotSafe = (ref, onNext, labelOrOpts = 'data') => {
  const label = typeof labelOrOpts === 'string' ? labelOrOpts : (labelOrOpts.label ?? 'data');
  const onError = typeof labelOrOpts === 'object' ? labelOrOpts.onError : null;
  return onSnapshot(ref, onNext, (error) => {
    console.error(`Firebase listener error (${label}):`, error);
    logDiag('listener-error', error, { label });
    if (onError) { onError(error); return; }
    const now = Date.now();
    if (now - lastErrorToast > 10000) {
      lastErrorToast = now;
      toast.error('Connection lost \u2014 data may be outdated. Retrying...');
    }
  });
};
